/**
 * @file OptionCards.tsx
 * @description Визуальные радиокнопки-карточки (phase19.16 Р1; uiux-design-r1
 * §1 «Форма кнопок», §2 «Расположение»): образец варианта сверху, подпись
 * снизу. По две в ряд (от 156px, высота от 96), больше — только когда
 * хватает ширины САМОГО блока (container query, а не ширины окна): в
 * панели конструктора 380 на компьютере три карточки не помещались и
 * третья уходила за край (замечание владельца 2026-10-11).
 */

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface OptionCard<T extends string> {
  value: T;
  label: string;
  /** Образец варианта (мини-схема, кнопка). */
  preview: ReactNode;
  hint?: string;
  /** Недоступно (причина — в `hint`): «По фото» без фото (SB120 этап 1). */
  disabled?: boolean;
}

interface Props<T extends string> {
  options: readonly OptionCard<T>[];
  value: T;
  onChange: (value: T) => void;
  "aria-label": string;
  /** Колонок, когда ширины блока хватает (3 — от 480px, 4 — от 640px). */
  columns?: 2 | 3 | 4;
}

const COLUMNS_CLASS: Readonly<Record<2 | 3 | 4, string>> = {
  2: "",
  3: "@min-[480px]:grid-cols-3",
  4: "@min-[480px]:grid-cols-3 @min-[640px]:grid-cols-4",
};


export function OptionCards<T extends string>({ options, value, onChange, columns = 3, "aria-label": ariaLabel }: Props<T>) {
  return (
    // Свой контейнер: число колонок — по ширине блока, а не окна.
    <div className="@container min-w-0">
      <div role="radiogroup" aria-label={ariaLabel} data-columns={columns} className={cn("grid grid-cols-2 gap-2", COLUMNS_CLASS[columns])}>
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={option.disabled}
              onClick={() => onChange(option.value)}
              className={cn(
                "flex min-h-24 min-w-0 flex-col gap-2 rounded-xl bg-surface-lowest p-3 text-left outline-none transition-colors",
                "focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                active
                  ? "[box-shadow:inset_0_0_0_2px_var(--primary)]"
                  : "[box-shadow:inset_0_0_0_1px_var(--border)] hover:bg-surface-low",
              )}
            >
              <span aria-hidden className="flex min-h-10 items-center justify-center overflow-hidden rounded-lg bg-surface-low p-2">
                {option.preview}
              </span>
              <span className="type-body break-words font-medium text-on-surface">{option.label}</span>
              {option.hint ? <span className="type-caption text-on-surface-variant">{option.hint}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
